<template>
  <!-- Bubble -->
  <Teleport to="#app">
    <transition>
      <button
        v-show="! eventsInView && ! $root.heroInView"
        class="a-bubble"
        type="button"
        @click.prevent="scrollToEvents"
      >
        <span
          class="a-bubble__text"
          v-text="total
            ? total === 1 ? '1 ' + $t('events_overview.result') : total + ' ' + $t('events_overview.results')
            : $t('events_overview.no_results')
          ">
        </span>
        <svg class="a-bubble__icon" aria-hidden="true">
          <use :xlink:href="$root.spritePath + '#btn-arrow-up'" />
        </svg>
      </button>
    </transition>
  </Teleport>

  <!-- Mobile filters -->
  <Teleport to="#app">
    <transition>
      <div
        v-if="modalActive"
        ref="mobileFilters"
        class="m-filters-mobile"
      >
        <div class="m-filters-mobile__inner">
          <div class="m-filters-mobile__header">
            <h2 class="a-h2 m-filters-mobile__title">{{ $t('events_overview.filter') }}</h2>

            <button
              class="m-filters-mobile__reset"
              type="button"
              @click.prevent="resetAndLoad"
            >
              {{ $t('events_overview.reset_filters') }}
            </button>
          </div>
          <div class="m-filters-mobile__main">
            <!-- Filters - Mobile -->
            <div class="m-filters">
              <BaseEventsFilter
                v-if="filters"
                :filters="filters"
                v-model:filterDistricts="filterDistricts"
                v-model:filterMonths="filterMonths"
                v-model:filterYears="filterYears"
                @update:filterDistricts=";[setPage(1), load()]"
                @update:filterMonths=";[setPage(1), load()]"
                @update:filterYears=";[setPage(1), load()]"
              ></BaseEventsFilter>
            </div>
          </div>
          <div class="m-filters-mobile__footer">
            <button
              type="button"
              @click.prevent="toggleFilters"
              class="a-btn-pill"
              v-text="$t('filters.close')"
            ></button>
          </div>
        </div>
        <button
          type="button"
          @click.prevent="toggleFilters"
          class="m-filters-mobile__backdrop"
        ></button>
      </div>
    </transition>
  </Teleport>

  <!-- Overview -->
  <div id="events-content" class="a-component o-container o-layout">
    <div
      v-if="hasFilters"
      class="o-overview__mobile-header u-col-start:1 u-col-end:13"
    >
      <button type="button" @click.prevent="toggleFilters" class="a-btn-pill">{{ $t('events_overview.filter') }}</button>
    </div>

    <!-- Filters - Desktop -->
    <div
      v-if="!modalActive && (loading || hasFilters)"
      class="m-filters m-filters--horizontal u-col-start:1@lg u-col-end:13@lg u-hidden@md-down"
    >
      <a class="u-skip-link" href="#events-overview">
        {{ $t('skip_filters') }}
      </a>

      <h2 id="events-filters-label" class="a-h5 u-mt-0 u-mr-2">
        Filter op:
      </h2>

      <BaseEventsFilter
        v-if="filters"
        :as-dropdown="true"
        :filters="filters"
        v-model:filterDistricts="filterDistricts"
        v-model:filterMonths="filterMonths"
        v-model:filterYears="filterYears"
        @update:filterDistricts=";[setPage(1), load()]"
        @update:filterMonths=";[setPage(1), load()]"
        @update:filterYears=";[setPage(1), load()]"
        aria-labelledby="events-filters-label"
      ></BaseEventsFilter>

      <div v-else class="m-filters__group m-filters__group--skeleton"></div>
    </div>

    <!-- Main -->
    <div
      v-element-visibility="onElementVisibility"
      class="u-col-start:first u-col-end:last"
      id="events-overview"
    >
      <!-- Loading -->
      <template v-if="loading">
        <div class="o-tile-list">
          <div v-for="i in Array(12)" :key="i">
            <div class="m-tile m-tile--list m-tile--skeleton o-tile-list__item">
              <div class="m-tile__figure"></div>

              <div class="m-tile__content">
                <div class="m-tile__header">
                  <div class="a-h3 m-tile__title"></div>
                </div>

                <div class="m-tile__main"></div>

                <div class="m-tile__details">
                  <span class="m-tile__details-date">
                    <span></span>
                  </span>
                  <span class="m-tile__details-location">
                    <span></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Error after loading -->
      <template v-else-if="loadingError">
        <div class="m-paragraph o-editor-content">
          <p>
            {{ $t('events_overview.loading_error') }}
          </p>
        </div>
      </template>

      <!-- No results -->
      <template v-else-if="items.length === 0">
        <div class="m-paragraph o-editor-content">
          <p>
            {{ $t('events_overview.no_content') }}
          </p>
        </div>
      </template>

      <!-- Items -->
      <template v-else>
        <div class="o-tile-list">
          <template v-for="entry in items" :key="entry.id">
            <component
              :is="entry.archive ? 'div' : 'a'"
              :href="entry.url"
              class="m-tile m-tile--list o-tile-list__item"
            >
              <ul v-if="entry.tags.length" class="m-tile__tags">
                <li v-for="(tag, index) in entry.tags" :key="index">
                  <div
                    class="m-tile__tag a-tag a-tag--sm"
                    v-text="tag.title"
                  ></div>
                </li>
              </ul>

              <div class="m-tile__figure">
                <picture v-if="entry.image" class="a-image m-tile__image">
                  <source :data-srcset="entry.image.srcset" />
                  <img
                    class="a-image__img"
                    :data-src="entry.image.src"
                    :alt="entry.image.alt"
                    loading="lazy"
                  />
                </picture>
              </div>

              <div class="m-tile__content">
                <div class="m-tile__header">
                  <h2
                    class="a-h3 m-tile__title"
                    v-text="entry.title"
                  ></h2>
                </div>

                <div
                  v-if="entry.subtitle"
                  class="m-tile__main"
                >
                  <p v-text="entry.subtitle"></p>
                </div>

                <div class="m-tile__details">
                  <span
                    v-if="entry.formatted_date"
                    class="m-tile__details-date"
                  >
                    <BaseTooltip>
                      <template #action>
                        <div class="a-icon-label">
                          <svg class="a-icon-label__icon" aria-hidden="true">
                            <use :xlink:href="$root.spritePath + '#date'"/>
                          </svg>
                          <span
                            class="a-icon-label__text"
                            v-text="entry.formatted_date"
                          ></span>
                        </div>
                      </template>

                      <template #tooltip>
                        {{ entry.archive
                          ? $t('events_overview.last_moment')
                          : $t('events_overview.upcoming_moment')
                        }}
                      </template>
                    </BaseTooltip>

                    <template v-if="entry.formatted_start_time">
                      <div class="a-icon-label">
                        <svg class="a-icon-label__icon" aria-hidden="true">
                          <use :xlink:href="$root.spritePath + '#clock'"/>
                        </svg>
                        <span
                          class="a-icon-label__text"
                          v-text="entry.formatted_start_time"
                        ></span>
                      </div>
                    </template>

                    <template v-if="entry.formatted_dates_relevant.length">
                      <BaseTooltip>
                        <template #action>
                          <div class="a-icon-label">
                            <svg class="a-icon-label__icon" aria-hidden="true">
                              <use
                                :xlink:href="! entry.archive
                                  ? $root.spritePath + '#next'
                                  : $root.spritePath + '#prev'
                                "
                              />
                            </svg>
                            <span class="a-icon-label__text">
                              <span
                                class="u-hidden@lg-only u-hidden@lgxx"
                                v-text="! entry.archive ? $t('events_overview.next') : $t('events_overview.prev')"
                              ></span>
                              <span
                                class="u-hidden@md-down u-hidden@lgx-only"
                                v-text="! entry.archive ? $t('events_overview.next_dates') : $t('events_overview.prev_dates')"
                              ></span>
                            </span>
                          </div>
                        </template>
                        <template #tooltip>
                          <span
                            v-for="(relevant, index) in (entry.archive
                              ? entry.formatted_dates_relevant
                              : entry.formatted_dates_relevant.slice(0, 3)
                            )"
                            :key="index"
                          >
                            {{ relevant }}<br>
                          </span>
                          <span v-if="! entry.archive && entry.formatted_dates_relevant.length > 3">…</span>
                        </template>
                      </BaseTooltip>
                    </template>
                  </span>

                  <span
                    v-if="entry.location"
                    class="m-tile__details-location"
                  >
                    <span class="a-icon-label">
                      <svg class="a-icon-label__icon" aria-hidden="true">
                        <use :xlink:href="$root.spritePath + '#location'"/>
                      </svg>
                      <span
                        class="a-icon-label__text"
                        v-text="entry.location"
                      ></span>
                    </span>
                  </span>
                </div>
              </div>
            </component>
          </template>
        </div>
      </template>

      <BasePagination
        v-if="totalPages > 1"
        v-model="filterPage"
        :total="total"
        :total-pages="totalPages"
        @update:modelValue=";[scrollToEvents(), load()]"
      ></BasePagination>
    </div>
  </div>
</template>

<script>
import BaseEventsFilter from './BaseEventsFilter.vue'
import BasePagination from './BasePagination.vue'
import BaseTooltip from './BaseTooltip.vue'
import allyMaintainDisabled from 'ally.js/maintain/disabled'
import allyWhenKey from 'ally.js/when/key'
import axios from 'axios'
import { vueDebounce } from 'vue-debounce'
import { vElementVisibility } from '@vueuse/components'

export default {
  directives: {
    debounce: vueDebounce({ lock: true }),
    ElementVisibility: vElementVisibility,
  },

  components: {
    BaseEventsFilter,
    BasePagination,
    BaseTooltip,
  },

  props: {
    overview: {
      type: String,
      default: 'agenda',
    },

    archive: {
      type: Boolean,
      default: false,
    },

    searchPlaceholder: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    items: [],
    filters: null,
    total: "…",
    totalPages: null,
    eventsInView: true,

    search: "",

    filterPage: 1,
    filterDistricts: [],
    filterMonths: [],
    filterYears: [],

    loading: true,
    loadingError: false,
    modalActive: false,
    disabledHandle: null,
    keyHandle: null,
  }),

  computed: {
    hasFilters() {
      if (! this.filters) {
        return false
      }

      return this.filters.districts.filter((i) => i.count).length ||
             this.filters.months.filter((i) => i.count).length ||
             this.filters.years.filter((i) => i.count).length
    },
  },

  async mounted() {
    this.loadParams()
    this.load()
  },

  methods: {
    loadParams() {
      const params = new URLSearchParams(window.location.search)

      if (params.get("q")) {
        this.search = params.get("q")
      }

      if (params.get("page")) {
        this.filterPage = parseInt(params.get("page"), 10)
      }

      if (params.get("districts")) {
        this.filterDistricts = params.get("districts").split(',').map((value) => value.trim())
      }

      if (params.get("months")) {
        this.filterMonths = params.get("months").split(',').map((value) => value.trim())
      }

      if (params.get("years")) {
        this.filterYears = params.get("years").split(',').map((value) => value.trim())
      }
    },

    updateParams() {
      const params = new URLSearchParams(window.location.search)

      this.search !== ""
        ? params.set("q", this.search)
        : params.delete("q")

      this.filterPage !== 1
        ? params.set("page", this.filterPage)
        : params.delete("page")

      this.filterDistricts.length
        ? params.set("districts", this.filterDistricts.join(','))
        : params.delete("districts")

      this.filterMonths.length
        ? params.set("months", this.filterMonths.join(','))
        : params.delete("months")

      this.filterYears.length
        ? params.set("years", this.filterYears.join(','))
        : params.delete("years")

      // Update state without adding history.
      const newParams = params.toString()

      newParams === ""
        ? history.replaceState({}, null, window.location.pathname)
        : history.replaceState({}, null, window.location.pathname + "?" + newParams)
    },

    resetAndLoad() {
      this.setPage(1)
      this.resetFilters()
      this.load()
    },

    resetFilters() {
      this.filterDistricts = []
    },

    setPage(page) {
      // Scroll to top of the page when the page differs.
      if (this.filterPage !== page) {
        this.scrollToEvents()
      }

      this.filterPage = page
    },

    scrollToEvents() {
      const duration = 500,
            bodyRect = document.body.getBoundingClientRect(),
            elemRect = document.body.querySelector('#events-content').getBoundingClientRect(),
            startingY = window.scrollY,
            offset = elemRect.top - bodyRect.top,
            diff = offset - startingY - 150

      let start

      // Bootstrap our animation - it will get called right before next frame shall be rendered.
      window.requestAnimationFrame(function step(timestamp) {
        if (!start) start = timestamp;
        // Elapsed milliseconds since start of scrolling.
        const time = timestamp - start;
        // Get percent of completion in range [0, 1].
        const percent = Math.min(time / duration, 1);

        window.scrollTo(0, startingY + diff * percent);

        // Proceed with animation as long as we wanted it to.
        if (time < duration) {
          window.requestAnimationFrame(step);
        }
      })
    },

    async load() {

      // Add a small delay to allow scrolling to the top before load.
      setTimeout(async() => {
        // Update the url params.
        this.updateParams()

        // Set the states.
        this.loading = true
        this.loadingError = false

        try {
          const response = await axios.get(window.siteUrl.replace(/\/$/, '') + "/api/v1/events", {
            params: {
              q: this.search,
              page: this.filterPage,
              archive: this.archive ? 1 : '',
              districts: this.filterDistricts.join(","),
              months: this.filterMonths.join(","),
              years: this.filterYears.join(","),
              overview: this.overview,
            },
          })

          this.items = response.data.entries
          this.filters = response.data.filters
          this.total = response.data.pagination.total
          this.totalPages = response.data.pagination.total_pages
        } catch (e) {
          this.total = 0
          this.loadingError = true
        } finally {
          this.loading = false
          this.$nextTick(() => this.$root.lazyLoad.update())
        }
      }, 200);
    },

    openFilters() {
      this.modalActive = true

      // https://github.com/medialize/ally.js/issues/139
      setTimeout(() => {
        this.disabledHandle = allyMaintainDisabled({
          filter: [this.$refs.mobileFilters],
        })

        this.keyHandle = allyWhenKey({
          escape: () => this.closeFilters(),
        })
      })
    },

    closeFilters() {
      this.modalActive = false
      this.disabledHandle ? this.disabledHandle.disengage() : null
      this.keyHandle ? this.keyHandle.disengage() : null
    },

    toggleFilters() {
      this.modalActive ? this.closeFilters() : this.openFilters()
    },

    onElementVisibility(value) {
      if (this.loading) {
        return
      }

      this.eventsInView = value
    },
  },
}
</script>
